import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Container,
  HeaderBar,
  Loader,
  Toast as toast,
  TypoGraph,
} from "components";
import {
  deleteSftpReportConfig,
  getSftpReportConfigs,
  runSftpReport,
} from "api";
import { ISftpReportConfigResponse } from "interfaces";
import styles from "./reportIntegrations.module.scss";

const ReportIntegrationsListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<ISftpReportConfigResponse[]>([]);
  const [runOpenForId, setRunOpenForId] = useState<number | null>(null);
  const [runStart, setRunStart] = useState("");
  const [runEnd, setRunEnd] = useState("");
  const [runSubmitting, setRunSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getSftpReportConfigs();
      setConfigs(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sorted = useMemo(
    () =>
      [...configs].sort((a, b) => a.companySlug.localeCompare(b.companySlug)),
    [configs]
  );

  const handleDelete = async (cfg: ISftpReportConfigResponse) => {
    const ok = window.confirm(
      `Delete SFTP report config for "${cfg.displayName}"? This cannot be undone.`
    );
    if (!ok) {
      return;
    }
    try {
      await deleteSftpReportConfig(cfg.id);
      toast({ title: "Deleted", subTitle: `${cfg.displayName} removed` });
      await load();
    } catch (e) {
      // request layer surfaces error toast
    }
  };

  const openRunDialog = (id: number) => {
    setRunOpenForId(id);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const iso = yesterday.toISOString().slice(0, 10);
    setRunStart(iso);
    setRunEnd(iso);
  };

  const submitRun = async () => {
    if (runOpenForId == null) {
      return;
    }
    const target = configs.find((c) => c.id === runOpenForId);
    if (!target) {
      return;
    }
    setRunSubmitting(true);
    try {
      await runSftpReport({
        companySlug: target.companySlug,
        windowStartDate: runStart,
        windowEndDate: runEnd,
      });
      toast({
        title: "Queued",
        subTitle: `Re-run queued for ${target.displayName}`,
      });
      setRunOpenForId(null);
    } finally {
      setRunSubmitting(false);
    }
  };

  return (
    <Container className={styles.container}>
      <HeaderBar title="Report Integrations" />
      <Loader loading={loading} />

      <Container className={styles.toolbar}>
        <Button
          id="newReportIntegration"
          label="New integration"
          variant="contained"
          onClick={() => navigate("/settings/report-integrations/new")}
        />
      </Container>

      <Container className={styles.listContainer}>
        {sorted.map((cfg) => (
          <Card key={cfg.id} sxContent={{ display: "grid", gridGap: "8px" }}>
            <Container className={styles.row}>
              <Container className={styles.rowInfo}>
                <TypoGraph variant="h3" content={cfg.displayName} />
                <TypoGraph
                  variant="body2"
                  content={`Slug: ${cfg.companySlug} · Report: ${cfg.reportType} · Brokers: ${cfg.brokerCompanyIdsCsv}`}
                />
                <TypoGraph
                  variant="body2"
                  content={`Schedule: ${cfg.cronExpression} (${cfg.cronTimezone}) · Basis: ${cfg.dateWindowBasis}`}
                />
                <TypoGraph
                  variant="body2"
                  content={`SFTP: ${cfg.sftpUsername}@${cfg.sftpHost}:${cfg.sftpPort}${cfg.sftpRemoteDirectory}`}
                />
                <TypoGraph
                  variant="body2"
                  content={`Filename: ${cfg.filenamePattern} · Delimiter: "${cfg.csvDelimiter}"`}
                />
                <span
                  className={
                    cfg.enabled ? styles.statusEnabled : styles.statusDisabled
                  }
                >
                  {cfg.enabled ? "Enabled" : "Disabled"}
                </span>
                {cfg.lastSuccessAt && (
                  <TypoGraph
                    variant="body2"
                    content={`Last success: ${cfg.lastSuccessAt}`}
                  />
                )}
                {cfg.lastErrorMessage && (
                  <span className={styles.statusError}>
                    Last error: {cfg.lastErrorMessage}
                  </span>
                )}
              </Container>

              <Container className={styles.rowActions}>
                <Button
                  id={`editReportIntegration-${cfg.id}`}
                  label="Edit"
                  variant="outlined"
                  onClick={() =>
                    navigate(`/settings/report-integrations/${cfg.id}`)
                  }
                />
                <Button
                  id={`runReportIntegration-${cfg.id}`}
                  label="Run now"
                  variant="outlined"
                  onClick={() => openRunDialog(cfg.id)}
                />
                <Button
                  id={`deleteReportIntegration-${cfg.id}`}
                  label="Delete"
                  variant="outlined"
                  onClick={() => handleDelete(cfg)}
                />
              </Container>
            </Container>

            {runOpenForId === cfg.id && (
              <Container className={styles.runDialog}>
                <TypoGraph
                  variant="h4"
                  content={`Re-run ${cfg.displayName} for date range`}
                />
                <label className={styles.label}>
                  <TypoGraph variant="body2" content="Window start (yyyy-MM-dd)" />
                  <input
                    className={styles.input}
                    value={runStart}
                    onChange={(e) => setRunStart(e.target.value)}
                  />
                </label>
                <label className={styles.label}>
                  <TypoGraph variant="body2" content="Window end (yyyy-MM-dd)" />
                  <input
                    className={styles.input}
                    value={runEnd}
                    onChange={(e) => setRunEnd(e.target.value)}
                  />
                </label>
                <Container className={styles.formActions}>
                  <Button
                    id={`confirmRun-${cfg.id}`}
                    label="Queue run"
                    variant="contained"
                    loading={runSubmitting}
                    disabled={!runStart || !runEnd}
                    onClick={submitRun}
                  />
                  <Button
                    id={`cancelRun-${cfg.id}`}
                    label="Cancel"
                    variant="outlined"
                    onClick={() => setRunOpenForId(null)}
                  />
                </Container>
              </Container>
            )}
          </Card>
        ))}
      </Container>
    </Container>
  );
};

export default ReportIntegrationsListPage;
